/**
 * Typed event bus.
 *
 * The important one is `noise`. A single NoiseEvent drives BOTH the audio mix
 * and granny's hearing sensor, so what the player hears is exactly what granny
 * hears. That coupling is what makes the stealth feel honest instead of
 * arbitrary - never raise the volume of something without also making it
 * audible to her, or vice versa.
 */

import type { Vec3 } from './math';

export type HeatTier = 'standby' | 'suspicious' | 'hunting' | 'frenzy';

export type NoiseSource = 'cat' | 'prop' | 'granny' | 'world';

export interface NoiseEvent {
  position: Vec3;
  /** 0-100. Compared against granny's hearing threshold AFTER attenuation. */
  loudness: number;
  source: NoiseSource;
  /** Audio cue id, e.g. 'ceramic_shatter'. Resolves through the audio manifest. */
  tag: string;
}

export type TraceKind =
  | 'spill'
  | 'shards'
  | 'debris'
  | 'scratches'
  | 'missing_item'
  | 'tp_mess'
  | 'soil'
  | 'flood'
  | 'door_ajar';

export interface GameEvents {
  /** Something made a sound. Audio and granny's ears both listen to this. */
  noise: NoiseEvent;

  /** The cat did something naughty. Heat system scores it. */
  mischief: {
    actionId: string;
    position: Vec3;
    propId?: string;
    /** Resolved by the heat system, not the caller: was granny looking? */
    witnessed?: boolean;
  };

  'prop:broken': { propId: string; position: Vec3; material: string };
  'prop:toppled': { propId: string; position: Vec3 };

  'trace:created': { traceId: string; kind: TraceKind; position: Vec3; roomId: string };
  'trace:found': { traceId: string };
  'trace:resolved': { traceId: string };

  'heat:changed': { heat: number; delta: number; reason: string };
  'heat:tier': { from: HeatTier; to: HeatTier; heat: number };
  'contact:changed': { contact: number };

  'cat:spotted': { by: string; position: Vec3 };
  'cat:lost': { by: string; lastKnown: Vec3 };
  'cat:sprayed': { position: Vec3 };
  'cat:caught': { by: string; position: Vec3 };
  'cat:ragdoll': { reason: 'spray' | 'fall' | 'catch' | 'vacuum' | 'debug'; impulse: Vec3 };
  'cat:recovered': Record<string, never>;
  /**
   * `witnessed` is the whole risk of the mechanic: diving into a box she is
   * watching does not conceal you, and the player has to be told which of the two
   * just happened.
   */
  'hideSpot:entered': { spotId: string; witnessed: boolean };
  'hideSpot:left': { spotId: string };

  'granny:activity': {
    activityId: string;
    phase: 'start' | 'interrupt' | 'resume' | 'end';
  };
  'granny:state': { from: string; to: string };
  /** Calm granny stopped for a fuss. The one nice thing she ever does to you. */
  'granny:petted': { position: Vec3; cozyPoints: number };

  'key:collected': { keyId: string; flavor: 'plain' | 'mischief' | 'granny' };
  'objective:progress': { objectiveId: string; current: number; target: number };
  'objective:complete': { objectiveId: string };
  'catMoment:completed': { momentId: string; cozyPoints: number };

  'level:loaded': { levelId: string };
  'level:won': { levelId: string };
  'level:failed': { levelId: string; reason: 'caught' };

  /** Presentation-only. Systems fire these; only src/render and src/ui listen. */
  'vfx:pop': { position: Vec3; text: string; color?: string };
  'vfx:dust': { position: Vec3; amount: number };
  'camera:shake': { amount: number };
}

export type EventName = keyof GameEvents;
export type Listener<K extends EventName> = (payload: GameEvents[K]) => void;

export class EventBus {
  private listeners = new Map<EventName, Set<Listener<EventName>>>();

  on<K extends EventName>(event: K, listener: Listener<K>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener as Listener<EventName>);
    return () => this.off(event, listener);
  }

  /** Auto-unsubscribing single-shot listener. */
  once<K extends EventName>(event: K, listener: Listener<K>): () => void {
    const off = this.on(event, (payload) => {
      off();
      listener(payload);
    });
    return off;
  }

  off<K extends EventName>(event: K, listener: Listener<K>): void {
    this.listeners.get(event)?.delete(listener as Listener<EventName>);
  }

  emit<K extends EventName>(event: K, payload: GameEvents[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    // Copy before iterating: listeners are allowed to unsubscribe themselves.
    for (const listener of [...set]) {
      try {
        (listener as Listener<K>)(payload);
      } catch (error) {
        // One broken listener must never take down the simulation step.
        console.error(`[cat-game] listener for "${event}" threw:`, error);
      }
    }
  }

  /** Drops every listener. Called on level teardown to prevent leaks. */
  clear(): void {
    this.listeners.clear();
  }

  listenerCount(event: EventName): number {
    return this.listeners.get(event)?.size ?? 0;
  }
}

/** The shared bus. Sim tests construct their own so runs cannot bleed together. */
export const events = new EventBus();
